import { useMemo, useState } from 'react'
import { Betrag } from './Betrag'
import { Abrufausfall, useAbruf } from './Abruf'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { useDarf } from './Rechte'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import {
  bewegungsartname,
  bewertungsebenenname,
  gesellschaftsname,
  katalog,
  kontobezeichnung,
  kurswert,
  periodenname,
  t,
} from './sprache'
import type { HistorischeKursluecke, HistorischerKurs, HistorischerKurseintrag } from './typen'

/**
 * Die Tabelle der historischen Kurse je Gesellschaft (2.6 Abschnitt 2.12,
 * R1470–R1476).
 *
 * **Oben, was im Monat offen ist** — dieselben Gruppen, an denen der Lauf
 * anhält (R1472). Wo es einen Vorschlag gibt, steht er da; wo nicht, etwa beim
 * Mischkurs der Startzeile (R1475), ein Feld. Ein Knopf legt alles an (R1473).
 *
 * **Darunter die Tabelle einer Gesellschaft**: Kurs, Betrag und Sachverhalt
 * lassen sich ändern; „teilen“ legt eine zweite Zeile derselben Bewegung an —
 * zwei Kapitalerhöhungen im selben Monat sind zwei Zeilen mit eigenem Kurs.
 */
export function HistorischeKurse({
  kreis,
  ledger,
  periode,
  vorwahl,
  aufGeaendert,
}: {
  kreis: string
  ledger: string
  periode: number
  /** Die Gesellschaft, die ein Verweis mitbringt (R1583) — sonst die erste mit offenen Zeilen. */
  vorwahl?: string
  aufGeaendert: () => void
}) {
  const darf = useDarf()
  // Die Spaltenbreiten zieht der Anwender (7.3 Abschnitt 10, R415).
  const { masse: offenmasse, rahmen: offenrahmen } = useSpaltenmasse<HTMLTableElement>('historische-kurse:offen')
  const { masse: tabellenmasse, rahmen: tabellenrahmen } = useSpaltenmasse<HTMLTableElement>('historische-kurse:tabelle')
  const [meldung, setMeldung] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [eingaben, setEingaben] = useState<Record<string, string>>({})
  const [gewaehlt, setGewaehlt] = useState(vorwahl ?? '')
  const [bearbeitet, setBearbeitet] = useState<{ schluessel: string; kurs: string; betrag: string; sachverhalt: string } | null>(null)

  const offen = useAbruf(
    kreis && ledger && periode ? () => api.historischeKurseOffen(kreis, ledger, periode) : null,
    [kreis, ledger, periode],
    true,
  )
  const gesellschaft = gewaehlt || offen.wert?.[0]?.einheit || ''
  const zeilen = useAbruf(
    gesellschaft ? () => api.historischeKurseDerGesellschaft(gesellschaft) : null,
    [gesellschaft],
    true,
  )

  const gesellschaften = useMemo(
    () => [...katalog().einheiten.values()].map((e) => ausStammdatum(e)),
    [],
  )

  async function tue(handgriff: () => Promise<unknown>) {
    setLaeuft(true)
    setMeldung(null)
    try {
      await handgriff()
      aufGeaendert()
    } catch (f) {
      setMeldung(fachlicherSatz(f))
    } finally {
      setLaeuft(false)
    }
  }

  const luecken = offen.wert ?? []
  const mehrereEbenen =
    new Set([...luecken.map((l) => l.ledger), ...(zeilen.wert ?? []).map((z) => z.ledger)]).size > 1

  function anlegen() {
    const eintraege: HistorischerKurseintrag[] = luecken
      .filter((l) => l.fehlt && (zahlAus(eingaben[lueckenkennung(l)] ?? '') ?? 0) > 0)
      .map((l) => ({
        einheit: l.einheit,
        ledger: l.ledger,
        konto: l.konto,
        bewegungsart: l.bewegungsart,
        kurs: zahlAus(eingaben[lueckenkennung(l)] ?? '') ?? 0,
      }))
    void tue(async () => {
      offen.setze(await api.historischeKurseAnlegen(kreis, ledger, periode, eintraege))
      setEingaben({})
      zeilen.erneut()
    })
  }

  function speichere(z: HistorischerKurs, aenderung: Partial<{ kurs: number | null; betrag: number; sachverhalt: string; loeschen: boolean }>, neu = false) {
    void tue(async () => {
      zeilen.setze(
        await api.historischerKursSetzen({
          schluessel: neu ? null : z.schluessel,
          einheit: z.einheit,
          ledger: z.ledger,
          konto: z.konto,
          periode: z.periode,
          bewegungsart: z.bewegungsart,
          sachverhalt: aenderung.sachverhalt ?? z.sachverhalt,
          betrag: aenderung.betrag ?? z.betrag,
          waehrung: z.waehrung,
          gegenwaehrung: z.gegenwaehrung,
          kurs: aenderung.kurs !== undefined ? aenderung.kurs : z.kurs,
          ergebnisjahr: z.ergebnisjahr,
          begruendung: null,
          loeschen: aenderung.loeschen ?? false,
        }),
      )
      setBearbeitet(null)
      offen.erneut()
    })
  }

  return (
    <>
      {/* **Offen im Monat** — nur, wenn etwas offen ist (R1473). */}
      {offen.ausgefallen && <Abrufausfall abruf={offen} />}
      {luecken.length > 0 && (
        <section>
          <h2>{t('historisch.offen.titel', { periode: periodenname(periode) })}</h2>
          <p className="erlaeuterung">{t('historisch.offen.erlaeuterung')}</p>
          <div className="tabellenrahmen">
            <table ref={offenrahmen} className="ziehbaretabelle">
              <Spaltenmassleiste masse={offenmasse} />
              <thead>
                <tr>
                  <th>{t('historisch.spalte.gesellschaft')}</th>
                  {mehrereEbenen && <th>{t('historisch.spalte.bewertungsebene')}</th>}
                  <th>{t('historisch.spalte.konto')}</th>
                  <th>{t('historisch.spalte.bewegungsart')}</th>
                  <th className="zahl">{t('historisch.spalte.geliefert')}</th>
                  <th className="zahl">{t('historisch.spalte.intabelle')}</th>
                  <th>{t('historisch.spalte.kurs')}</th>
                </tr>
              </thead>
              <tbody>
                {luecken.map((l) => (
                  <tr key={lueckenkennung(l)}>
                    <td>{l.gesellschaft}</td>
                    {mehrereEbenen && <td>{bewertungsebenenname(l.ledger)}</td>}
                    <td>{kontobezeichnung(l.konto)}</td>
                    <td>{l.bewegungsart ? bewegungsartname(l.bewegungsart) : t('historischer-kurs.startwert')}</td>
                    <td className="zahl"><Betrag wert={l.geliefert} waehrung={l.waehrung} /></td>
                    <td className="zahl"><Betrag wert={l.inTabelle} /></td>
                    <td>
                      {!l.fehlt ? (
                        t('historisch.offen.summe')
                      ) : l.vorschlagsart === 'eintrittskurs' ? (
                        t('historisch.vorschlag.eintrittskurs')
                      ) : l.vorschlag !== null && eingaben[lueckenkennung(l)] === undefined ? (
                        <>
                          {/* Der Knopf steht mit Abstand zum Satz (R1584). */}
                          <span className="satzmitknopf">
                            {`${kurswert(l.vorschlag)} · ${t(`historisch.vorschlag.${l.vorschlagsart ?? 'stichtag'}`, { jahr: String(l.ergebnisjahr ?? '') })}`}
                            {darf('historischeKurseAnlegen') && (
                              <button type="button" className="schmal" onClick={() => setEingaben((e) => ({ ...e, [lueckenkennung(l)]: '' }))}>
                                {t('historisch.vorschlag.ueberschreiben')}
                              </button>
                            )}
                          </span>
                        </>
                      ) : darf('historischeKurseAnlegen') ? (
                        <input
                          inputMode="decimal"
                          aria-label={t('historisch.spalte.kurs')}
                          placeholder={t(l.bewegungsart ? 'historisch.eingabe.kurs' : 'historisch.eingabe.mischkurs', { waehrung: l.waehrung, konzernwaehrung: l.gegenwaehrung })}
                          value={eingaben[lueckenkennung(l)] ?? ''}
                          onChange={(e) => setEingaben((alt) => ({ ...alt, [lueckenkennung(l)]: e.target.value }))}
                        />
                      ) : (
                        t('historisch.vorschlag.keiner')
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {darf('historischeKurseAnlegen') && (
            <div className="aktionsreihe">
              <button
                type="button"
                className="haupt"
                disabled={laeuft || Object.values(eingaben).some((w) => w.trim() !== '' && zahlAus(w) === null)}
                onClick={anlegen}
              >
                {t('historisch.anlegen')}
              </button>
            </div>
          )}
        </section>
      )}

      <section>
        <h2>{t('historisch.tabelle.titel')}</h2>
        <label className="feld einzeln">
          <span>{t('historisch.spalte.gesellschaft')}</span>
          <Auswahlfeld
            wert={gesellschaft}
            eintraege={gesellschaften}
            leer={t('historisch.gesellschaft.waehlen')}
            beschriftung={t('historisch.spalte.gesellschaft')}
            aufWahl={setGewaehlt}
          />
        </label>
        {zeilen.ausgefallen && <Abrufausfall abruf={zeilen} />}
        {zeilen.wert !== null && zeilen.wert.length === 0 && (
          <p className="leerhinweis">{t('historisch.tabelle.leer', { gesellschaft: gesellschaftsname(gesellschaft) })}</p>
        )}
        {zeilen.wert !== null && zeilen.wert.length > 0 && (
          <div className="tabellenrahmen">
            <table ref={tabellenrahmen} className="ziehbaretabelle">
              <Spaltenmassleiste masse={tabellenmasse} />
              <thead>
                <tr>
                  <th>{t('historisch.spalte.monat')}</th>
                  {mehrereEbenen && <th>{t('historisch.spalte.bewertungsebene')}</th>}
                  <th>{t('historisch.spalte.konto')}</th>
                  <th>{t('historisch.spalte.bewegungsart')}</th>
                  <th>{t('historisch.spalte.sachverhalt')}</th>
                  <th className="zahl">{t('historisch.spalte.betrag')}</th>
                  <th className="zahl">{t('historisch.spalte.kurs')}</th>
                  <th className="zahl">{t('historisch.spalte.konzern')}</th>
                  <th>{t('historisch.spalte.herkunft')}</th>
                  {darf('historischerKursSetzen') && <th />}
                </tr>
              </thead>
              <tbody>
                {zeilen.wert.map((z) =>
                  bearbeitet?.schluessel === z.schluessel ? (
                    <tr key={z.schluessel}>
                      <td>{periodenname(z.periode)}</td>
                      {mehrereEbenen && <td>{bewertungsebenenname(z.ledger)}</td>}
                      <td>{kontobezeichnung(z.konto)}</td>
                      <td>{bewegungsartname(z.bewegungsart)}</td>
                      <td>
                        <input
                          aria-label={t('historisch.spalte.sachverhalt')}
                          value={bearbeitet.sachverhalt}
                          onChange={(e) => setBearbeitet({ ...bearbeitet, sachverhalt: e.target.value })}
                        />
                      </td>
                      <td className="zahl">
                        <input
                          inputMode="decimal"
                          aria-label={t('historisch.spalte.betrag')}
                          value={bearbeitet.betrag}
                          onChange={(e) => setBearbeitet({ ...bearbeitet, betrag: e.target.value })}
                        />
                      </td>
                      <td className="zahl">
                        {z.herkunft === 'eintrittskurs' ? (
                          t('historisch.herkunft.eintrittskurs')
                        ) : (
                          <input
                            inputMode="decimal"
                            aria-label={t('historisch.spalte.kurs')}
                            value={bearbeitet.kurs}
                            onChange={(e) => setBearbeitet({ ...bearbeitet, kurs: e.target.value })}
                          />
                        )}
                      </td>
                      <td />
                      <td />
                      <td className="handgriff">
                        <button
                          type="button"
                          className="schmal"
                          disabled={
                            laeuft
                            || zahlAus(bearbeitet.betrag) === null
                            || (z.herkunft !== 'eintrittskurs' && !(Number(zahlAus(bearbeitet.kurs)) > 0))
                          }
                          onClick={() =>
                            speichere(z, {
                              sachverhalt: bearbeitet.sachverhalt,
                              betrag: zahlAus(bearbeitet.betrag) ?? z.betrag,
                              kurs: z.herkunft === 'eintrittskurs' ? null : zahlAus(bearbeitet.kurs),
                            })
                          }
                        >
                          {t('historisch.speichern')}
                        </button>
                        <button type="button" className="schmal" disabled={laeuft} onClick={() => setBearbeitet(null)}>
                          {t('historisch.abbrechen')}
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={z.schluessel}>
                      <td>{periodenname(z.periode)}</td>
                      {mehrereEbenen && <td>{bewertungsebenenname(z.ledger)}</td>}
                      <td>{kontobezeichnung(z.konto)}</td>
                      <td>{bewegungsartname(z.bewegungsart)}</td>
                      <td>{z.sachverhalt}</td>
                      <td className="zahl"><Betrag wert={z.betrag} waehrung={z.waehrung} /></td>
                      <td className="zahl">{z.kurs === null ? '' : kurswert(z.kurs)}</td>
                      <td className="zahl">
                        {z.kurs !== null && (
                          <Betrag wert={Math.round(z.betrag * z.kurs * 100) / 100} waehrung={z.gegenwaehrung} />
                        )}
                      </td>
                      <td className="gedaempft">{t(`historisch.herkunft.${z.herkunft}`)}</td>
                      {darf('historischerKursSetzen') && (
                        <td className="handgriff">
                          <button
                            type="button"
                            className="schmal"
                            disabled={laeuft}
                            onClick={() =>
                              setBearbeitet({
                                schluessel: z.schluessel,
                                kurs: z.kurs === null ? '' : String(z.kurs).replace('.', ','),
                                betrag: String(z.betrag).replace('.', ','),
                                sachverhalt: z.sachverhalt,
                              })
                            }
                          >
                            {t('historisch.aendern')}
                          </button>
                          {z.herkunft !== 'eintrittskurs' && (
                            <button
                              type="button"
                              className="schmal"
                              disabled={laeuft}
                              onClick={() => speichere(z, { betrag: 0, sachverhalt: `${z.sachverhalt} (2)` }, true)}
                            >
                              {t('historisch.teilen')}
                            </button>
                          )}
                          <button
                            type="button"
                            className="schmal gefahr"
                            disabled={laeuft}
                            onClick={() => speichere(z, { loeschen: true })}
                          >
                            {t('historisch.loeschen')}
                          </button>
                        </td>
                      )}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {meldung && <p className="meldung">{meldung}</p>}
    </>
  )
}

/** Eine Lücke, eindeutig im Monat: Gesellschaft, Bewertungsebene, Konto, Bewegungsart. */
export function lueckenkennung(l: Pick<HistorischeKursluecke, 'einheit' | 'ledger' | 'konto' | 'bewegungsart'>): string {
  return `${l.einheit}|${l.ledger}|${l.konto}|${l.bewegungsart ?? ''}`
}

/**
 * Eine Zahl, wie sie getippt wird — mit Komma, mit oder ohne Tausenderpunkt:
 * „1.234,56“, „0,8807“, „-100000“. `null`, wo keine Zahl steht.
 */
export function zahlAus(text: string): number | null {
  const roh = text.trim().replace(/\s/g, '')
  if (roh === '') return null
  const ohneTausender = roh.includes(',') ? roh.replace(/\./g, '').replace(',', '.') : roh
  const zahl = Number(ohneTausender)
  return Number.isFinite(zahl) ? zahl : null
}
